import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Alert,
  AlertTitle,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload,
  Download,
  CheckCircle,
  Error,
  Warning,
  Visibility,
  Close,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import customerService from '../services/customerService';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface ExcelImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImportComplete?: () => void;
}

interface ValidationResult {
  valid: boolean;
  errors: Array<{
    row: number;
    field: string;
    message: string;
    value: any;
  }>;
  warnings: Array<{
    row: number;
    field: string;
    message: string;
    value: any;
  }>;
  preview: Array<{
    name: string;
    email: string;
    phone: string;
    address: string;
    username: string;
    profile: string;
    router_name: string;
    package_name: string;
    spop_name: string;
    odp_code: string;
    generated_customer_code?: string;
  }>;
  summary: {
    totalRows: number;
    validRows: number;
    errorRows: number;
    warningRows: number;
  };
}

interface ImportResult {
  success: boolean;
  message: string;
  summary: {
    total: number;
    success: number;
    failed: number;
    skipped: number;
  };
  errors: Array<{
    row: number;
    error: string;
    data: any;
  }>;
  successfulCustomers: Array<{
    customer_code: string;
    name: string;
    email: string;
  }>;
}

const ExcelImportDialog: React.FC<ExcelImportDialogProps> = ({ open, onClose, onImportComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const queryClient = useQueryClient();

  const steps = [
    'Download Template',
    'Upload File',
    'Validate Data',
    'Import Customers'
  ];

  // Download template mutation
  const downloadTemplateMutation = useMutation({
    mutationFn: customerService.downloadCustomerTemplate,
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'customer-import-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // After downloading template, move to next step
      setActiveStep(1);
    },
  });

  // Validate file mutation
  const validateMutation = useMutation({
    mutationFn: customerService.previewCustomerExcel,
    onSuccess: (result) => {
      // Transform the result to match existing interface
      const transformedResult: ValidationResult = {
        valid: result.data.summary.errorRows === 0,
        errors: result.data.errors.flatMap((error: any) => 
          error.errors.map((err: string) => ({
            row: error.row,
            field: 'multiple',
            message: err,
            value: error.data
          }))
        ),
        warnings: [],
        preview: result.data.validData.map((item: any) => ({
          name: item.name,
          email: item.email,
          phone: item.phone,
          address: item.address,
          username: item.username,
          profile: item.profile,
          router_name: item._router?.name || 'Auto-selected',
          package_name: item._package?.name || 'No package',
          spop_name: item._spop?.name || '',
          odp_code: item._odp?.odpCode || '',
          generated_customer_code: item.customer_code
        })),
        summary: {
          totalRows: result.data.summary.totalRows,
          validRows: result.data.summary.validRows,
          errorRows: result.data.summary.errorRows,
          warningRows: 0
        }
      };
      setValidationResult(transformedResult);
      setActiveStep(2);
    },
    onError: (error: any) => {
      console.error('Validation error:', error);
      alert('Error validating file: ' + (error.response?.data?.message || error.message));
    },
  });

  // Import mutation
  const importMutation = useMutation({
    mutationFn: customerService.importCustomersFromExcel,
    onSuccess: (result) => {
      // Transform the result to match existing interface
      const transformedResult: ImportResult = {
        success: true,
        message: result.message,
        summary: {
          total: result.data.import.summary.total,
          success: result.data.import.summary.imported,
          failed: result.data.import.summary.failed,
          skipped: 0
        },
        errors: result.data.import.errors.map((error: any) => ({
          row: 0, // Backend doesn't provide row number
          error: error.error,
          data: error
        })),
        successfulCustomers: result.data.import.success.map((customer: any) => ({
          customer_code: customer.customer_code,
          name: customer.name,
          email: customer.email
        }))
      };
      setImportResult(transformedResult);
      setActiveStep(3);
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      
      // Call onImportComplete callback if provided
      if (onImportComplete) {
        onImportComplete();
      }
    },
    onError: (error: any) => {
      console.error('Import error:', error);
      alert('Error importing file: ' + (error.response?.data?.message || error.message));
    },
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setSelectedFile(file);
      setActiveStep(1);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
  });

  const handleValidate = () => {
    if (selectedFile) {
      setIsValidating(true);
      validateMutation.mutate(selectedFile, {
        onSettled: () => setIsValidating(false),
      });
    }
  };

  const handleImport = () => {
    if (selectedFile) {
      setIsImporting(true);
      importMutation.mutate(selectedFile, {
        onSettled: () => setIsImporting(false),
      });
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setSelectedFile(null);
    setValidationResult(null);
    setImportResult(null);
    setShowPreview(false);
    onClose();
  };

  const getStepperContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Download the Excel template first to ensure your data is in the correct format.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={() => downloadTemplateMutation.mutate()}
              disabled={downloadTemplateMutation.isPending}
            >
              {downloadTemplateMutation.isPending ? 'Downloading...' : 'Download Template'}
            </Button>
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                Template includes the following columns:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Required: name, email, phone, address, username, password, profile, spop_area_code, odp_code"
                    secondary="customer_code will be auto-generated based on SPOP + ODP + number"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Optional: package_name, status, activation_date"
                    secondary="Router will be selected automatically based on ODP"
                  />
                </ListItem>
              </List>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Paper
              {...getRootProps()}
              sx={{
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : 'grey.300',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
              }}
            >
              <input {...getInputProps()} />
              <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {isDragActive ? 'Drop the file here' : 'Drag & drop Excel file here'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                or click to select file (.xlsx, .xls)
              </Typography>
            </Paper>

            {selectedFile && (
              <Box mt={2}>
                <Alert severity="success">
                  <AlertTitle>File Selected</AlertTitle>
                  {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </Alert>
                <Box mt={2}>
                  <Button
                    variant="contained"
                    onClick={handleValidate}
                    disabled={isValidating}
                  >
                    {isValidating ? 'Validating...' : 'Validate & Preview'}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            {validationResult && (
              <>
                <Box mb={2}>
                  <Typography variant="h6" gutterBottom>
                    Validation Results
                  </Typography>
                  
                  <Box display="flex" gap={2} mb={2}>
                    <Chip 
                      label={`Total: ${validationResult.summary.totalRows}`} 
                      color="primary" 
                    />
                    <Chip 
                      label={`Valid: ${validationResult.summary.validRows}`} 
                      color="success" 
                    />
                    <Chip 
                      label={`Errors: ${validationResult.summary.errorRows}`} 
                      color="error" 
                    />
                    <Chip 
                      label={`Warnings: ${validationResult.summary.warningRows}`} 
                      color="warning" 
                    />
                  </Box>

                  <Box display="flex" gap={2} mb={2}>
                    <Button
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => setShowPreview(!showPreview)}
                    >
                      {showPreview ? 'Hide' : 'Show'} Preview
                    </Button>
                    
                    {validationResult.valid && (
                      <Button
                        variant="contained"
                        onClick={handleImport}
                        disabled={isImporting}
                        color="success"
                      >
                        {isImporting ? 'Importing...' : 'Import Data'}
                      </Button>
                    )}
                  </Box>
                </Box>

                {/* Errors */}
                {validationResult.errors.length > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <AlertTitle>Errors Found ({validationResult.errors.length})</AlertTitle>
                    <List dense>
                      {validationResult.errors.slice(0, 10).map((error, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <Error fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={`Row ${error.row}: ${error.field}`}
                            secondary={error.message}
                          />
                        </ListItem>
                      ))}
                      {validationResult.errors.length > 10 && (
                        <ListItem>
                          <ListItemText secondary={`... and ${validationResult.errors.length - 10} more errors`} />
                        </ListItem>
                      )}
                    </List>
                  </Alert>
                )}

                {/* Warnings */}
                {validationResult.warnings.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>Warnings ({validationResult.warnings.length})</AlertTitle>
                    <List dense>
                      {validationResult.warnings.slice(0, 5).map((warning, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <Warning fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={`Row ${warning.row}: ${warning.field}`}
                            secondary={warning.message}
                          />
                        </ListItem>
                      ))}
                      {validationResult.warnings.length > 5 && (
                        <ListItem>
                          <ListItemText secondary={`... and ${validationResult.warnings.length - 5} more warnings`} />
                        </ListItem>
                      )}
                    </List>
                  </Alert>
                )}

                {/* Preview */}
                {showPreview && validationResult.preview.length > 0 && (
                  <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Generated Code</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Username</TableCell>
                          <TableCell>Package</TableCell>
                          <TableCell>SPOP</TableCell>
                          <TableCell>ODP</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {validationResult.preview.slice(0, 20).map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold" color="primary">
                                {row.generated_customer_code || 'Will be generated'}
                              </Typography>
                            </TableCell>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>{row.email}</TableCell>
                            <TableCell>{row.phone}</TableCell>
                            <TableCell>{row.username}</TableCell>
                            <TableCell>{row.package_name}</TableCell>
                            <TableCell>{row.spop_name}</TableCell>
                            <TableCell>{row.odp_code}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            {importResult && (
              <>
                <Alert severity={importResult.success ? "success" : "error"} sx={{ mb: 2 }}>
                  <AlertTitle>
                    {importResult.success ? "Import Completed" : "Import Failed"}
                  </AlertTitle>
                  {importResult.message}
                </Alert>

                <Box display="flex" gap={2} mb={2}>
                  <Chip 
                    label={`Total: ${importResult.summary.total}`} 
                    color="primary" 
                  />
                  <Chip 
                    label={`Success: ${importResult.summary.success}`} 
                    color="success" 
                  />
                  <Chip 
                    label={`Failed: ${importResult.summary.failed}`} 
                    color="error" 
                  />
                  <Chip 
                    label={`Skipped: ${importResult.summary.skipped}`} 
                    color="warning" 
                  />
                </Box>

                {/* Successful Customers */}
                {importResult.successfulCustomers.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="h6" gutterBottom>
                      Successfully Imported Customers
                    </Typography>
                    <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Customer Code</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {importResult.successfulCustomers.map((customer, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold" color="primary">
                                  {customer.customer_code}
                                </Typography>
                              </TableCell>
                              <TableCell>{customer.name}</TableCell>
                              <TableCell>{customer.email}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Import Errors */}
                {importResult.errors.length > 0 && (
                  <Alert severity="error">
                    <AlertTitle>Import Errors ({importResult.errors.length})</AlertTitle>
                    <List dense>
                      {importResult.errors.slice(0, 10).map((error, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <Error fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={`Row ${error.row}`}
                            secondary={error.error}
                          />
                        </ListItem>
                      ))}
                      {importResult.errors.length > 10 && (
                        <ListItem>
                          <ListItemText secondary={`... and ${importResult.errors.length - 10} more errors`} />
                        </ListItem>
                      )}
                    </List>
                  </Alert>
                )}
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Import Customers from Excel</Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                optional={
                  index === 3 && importResult?.success ? (
                    <Typography variant="caption" color="success.main">
                      Import completed successfully
                    </Typography>
                  ) : null
                }
              >
                {label}
              </StepLabel>
              <StepContent>
                {getStepperContent(index)}
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {/* Progress indicators */}
        {(isValidating || isImporting) && (
          <Box mt={2}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" align="center" mt={1}>
              {isValidating ? 'Validating file...' : 'Importing customers...'}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          {importResult ? 'Close' : 'Cancel'}
        </Button>
        
        {activeStep > 0 && !importResult && (
          <Button 
            onClick={() => {
              if (activeStep === 1) {
                setSelectedFile(null);
                setActiveStep(0);
              } else if (activeStep === 2) {
                setValidationResult(null);
                setActiveStep(1);
              }
            }}
            disabled={isValidating || isImporting}
          >
            Back
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ExcelImportDialog;
